/**
 * Square OAuth2 Callback Page
 *
 * Square redirects here after merchant authorization:
 *   https://admin.velaro.com/integrations/square/callback?code=...&state=...
 *
 * This page:
 *   1. Reads code + state from URL query params
 *   2. POSTs to /api/admin/Square/OAuth/Callback to exchange the code for tokens
 *   3. On success: shows "Connected!" and redirects to the Square integration page
 *   4. On error: shows the error with a "Try again" link back to the Square page
 */
import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "@/hooks/useApi";

type CallbackState = "loading" | "success" | "error";

export default function SquareCallbackPage() {
  const api = useApi();
  const navigate = useNavigate();

  const [state, setState] = useState<CallbackState>("loading");
  const [merchantName, setMerchantName] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code  = params.get("code");
    const stateParam = params.get("state");
    const error = params.get("error");

    // Square sends error=access_denied if merchant cancels
    if (error) {
      setState("error");
      setErrorMessage(
        error === "access_denied"
          ? "Authorization was cancelled. You can try connecting again from the Square integration page."
          : `Square returned an error: ${error}. Please try again.`
      );
      return;
    }

    if (!code || !stateParam) {
      setState("error");
      setErrorMessage("Missing authorization code or state. Please start the connection process again.");
      return;
    }

    async function exchangeCode() {
      try {
        const resp = await api.admin.post("Square/OAuth/Callback", {
          code,
          state: stateParam
        });
        const data = await resp.json();

        if (!resp.ok) {
          setState("error");
          setErrorMessage(data?.error ?? "Failed to complete Square authorization. Please try again.");
          return;
        }

        setMerchantName(data.merchantName ?? "");
        setState("success");

        // Auto-redirect to Square page after 3 seconds
        setTimeout(() => {
          navigate("/integrations/square-appointments");
        }, 3000);
      } catch {
        setState("error");
        setErrorMessage("An unexpected error occurred. Please try connecting again.");
      }
    }

    exchangeCode();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 max-w-md w-full text-center space-y-5">

        {/* Square logo mark */}
        <div className="w-14 h-14 bg-black rounded-xl flex items-center justify-center text-white font-bold text-2xl mx-auto">
          S
        </div>

        {state === "loading" && (
          <>
            <h1 className="text-lg font-semibold text-slate-800">Connecting to Square…</h1>
            <p className="text-sm text-slate-500">Exchanging authorization code for your merchant tokens.</p>
            <div className="flex justify-center">
              <div className="w-6 h-6 border-2 border-slate-300 border-t-black rounded-full animate-spin" />
            </div>
          </>
        )}

        {state === "success" && (
          <>
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <i className="fa-solid fa-check text-green-600 text-lg" />
            </div>
            <h1 className="text-lg font-semibold text-slate-800">Connected to Square!</h1>
            {merchantName && (
              <p className="text-sm text-slate-600">
                <strong>{merchantName}</strong> is now connected to Velaro.
              </p>
            )}
            <p className="text-sm text-slate-500">
              Your Square calendar is ready. Redirecting you to finish setup…
            </p>
            <button
              onClick={() => navigate("/integrations/square-appointments")}
              className="text-sm text-blue-600 underline"
            >
              Go now →
            </button>
          </>
        )}

        {state === "error" && (
          <>
            <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
              <i className="fa-solid fa-xmark text-red-600 text-lg" />
            </div>
            <h1 className="text-lg font-semibold text-slate-800">Connection failed</h1>
            <p className="text-sm text-slate-600">{errorMessage}</p>
            <button
              onClick={() => navigate("/integrations/square-appointments")}
              className="inline-block bg-black text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-slate-800 transition-colors"
            >
              Back to Square Settings
            </button>
          </>
        )}
      </div>
    </div>
  );
}
